(() => {
  const selector = "[data-fourpaw-deferred-app]";

  const activateScripts = (fragment) => {
    const deferredScripts = [];

    fragment.querySelectorAll("script").forEach((original) => {
      const marker = document.createComment("fourpaw-deferred-app-script");
      original.replaceWith(marker);
      deferredScripts.push({ marker, original });
    });

    return deferredScripts;
  };

  const appHasRendered = (root) => {
    const iframe = root.querySelector("iframe");
    const appBlock = root.querySelector(".shopify-app-block");
    const renderedHeight = iframe?.getBoundingClientRect().height || appBlock?.getBoundingClientRect().height || 0;
    return renderedHeight > 100;
  };

  const markReady = (root) => {
    if (appHasRendered(root)) {
      root.classList.add("is-ready");
      root.setAttribute("aria-busy", "false");
      return true;
    }
    return false;
  };

  const waitUntilReady = (root) => {
    if (markReady(root)) return;

    const candidates = Array.from(root.querySelectorAll("iframe, .shopify-app-block"));
    if (candidates.length === 0) {
      root.classList.add("is-ready", "is-empty");
      root.setAttribute("aria-busy", "false");
      return;
    }

    if (!("ResizeObserver" in window)) {
      window.setTimeout(() => {
        markReady(root);
        root.setAttribute("aria-busy", "false");
      }, 8000);
      return;
    }

    let timeout = 0;
    const observer = new ResizeObserver(() => {
      if (!markReady(root)) return;
      observer.disconnect();
      window.clearTimeout(timeout);
    });
    candidates.forEach((candidate) => observer.observe(candidate));
    timeout = window.setTimeout(() => {
      observer.disconnect();
      root.setAttribute("aria-busy", "false");
    }, 8000);
  };

  const loadSection = (root) => {
    if (!root || root.dataset.loaded === "true") return;

    const template = root.querySelector("template[data-fourpaw-deferred-app-template]");
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    const deferredScripts = activateScripts(fragment);
    const placeholder = root.querySelector("[data-fourpaw-deferred-app-placeholder]");

    root.dataset.loaded = "true";
    template.replaceWith(fragment);
    placeholder?.remove();

    deferredScripts.forEach(({ marker, original }) => {
      const script = document.createElement("script");
      Array.from(original.attributes).forEach(({ name, value }) => script.setAttribute(name, value));
      script.textContent = original.textContent;
      marker.replaceWith(script);
    });

    root.classList.add("is-loaded");
    root.dispatchEvent(new CustomEvent("fourpaw:deferred-app-loaded", { bubbles: true }));
    waitUntilReady(root);
  };

  const observeSection = (root) => {
    if (!root || root.dataset.observed === "true") return;
    root.dataset.observed = "true";

    if (!("IntersectionObserver" in window)) {
      loadSection(root);
      return;
    }

    const distance = Number.parseInt(root.dataset.loadDistance || "2000", 10);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        loadSection(root);
      },
      { rootMargin: `${Number.isFinite(distance) ? distance : 2000}px 0px` },
    );

    observer.observe(root);
  };

  const init = (scope = document) => {
    if (scope.matches?.(selector)) observeSection(scope);
    scope.querySelectorAll?.(selector).forEach(observeSection);
  };

  const findReviewRoot = () =>
    document.querySelector(
      '[data-fourpaw-deferred-anchor="looxReviewsFrame"], [data-fourpaw-deferred-anchor="looxReviews"]',
    ) ||
    Array.from(document.querySelectorAll(selector)).find((root) => {
      if (root.querySelector("#looxReviews, #looxReviewsFrame")) return true;
      return Array.from(root.querySelectorAll("template")).some((template) =>
        template.content.querySelector("#looxReviews, #looxReviewsFrame"),
      );
    });

  const scrollToReviews = (root, attempt = 0) => {
    const target = root.querySelector("#looxReviewsFrame, #looxReviews") || root;
    const top = target.getBoundingClientRect().top + window.scrollY - 96;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({
      top: Math.max(0, top),
      // Keep the first movement polished, then correct layout shifts immediately.
      // Repeated smooth scrolls lag behind sections that hydrate above Loox.
      behavior: attempt === 0 && !prefersReducedMotion ? "smooth" : "auto",
    });

    const renderedHeight = target.getBoundingClientRect().height;
    if (renderedHeight > 100 || attempt >= 50) {
      target.setAttribute("tabindex", "-1");
      window.setTimeout(() => target.focus({ preventScroll: true }), 250);
      return;
    }
    window.setTimeout(() => scrollToReviews(root, attempt + 1), 100);
  };

  window.FourPawDeferredAppSections = { init, loadSection };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener("click", (event) => {
    const reviewLink = event.target.closest('a[href="#looxReviews"], a[href="#looxReviewsFrame"]');
    if (!reviewLink) return;

    const root = findReviewRoot();
    if (!root) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    loadSection(root);
    window.history.replaceState(null, "", "#looxReviewsFrame");
    scrollToReviews(root);
  }, { capture: true });

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
